import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** Plain text run between inline tag entities. */
export type TextSegment = { id: string; type: 'text'; value: string };

/** Opaque object shown as a tag; serialized with JSON.stringify over the wire. */
export type TagSegment = {
  id: string;
  type: 'tag';
  payload: Record<string, unknown>;
};

export type ChatComposerSegment = TextSegment | TagSegment;

function newSegmentId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `seg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Concatenate segments into one string for `executeProcessor.variables.input`:
 * text segments are appended as-is; tag segments as JSON.stringify(payload) with no delimiter.
 */
export function serializeDocumentChatWireString(
  segments: ChatComposerSegment[],
): string {
  return segments
    .map((s) => (s.type === 'text' ? s.value : JSON.stringify(s.payload)))
    .join('');
}

export function hasComposerContent(segments: ChatComposerSegment[]): boolean {
  for (const s of segments) {
    if (s.type === 'tag') return true;
    if (s.type === 'text' && s.value.trim().length > 0) return true;
  }
  return false;
}

function mergeAdjacentText(
  segments: ChatComposerSegment[],
): ChatComposerSegment[] {
  const out: ChatComposerSegment[] = [];
  for (const seg of segments) {
    if (seg.type === 'text' && out.length > 0) {
      const prev = out[out.length - 1];
      if (prev.type === 'text') {
        prev.value += seg.value;
        continue;
      }
    }
    out.push(
      seg.type === 'text'
        ? { id: seg.id, type: 'text', value: seg.value }
        : { id: seg.id, type: 'tag', payload: { ...seg.payload } },
    );
  }
  return out;
}

export interface DocumentChatComposerContextValue {
  segments: ChatComposerSegment[];
  setSegments: React.Dispatch<React.SetStateAction<ChatComposerSegment[]>>;
  updateTextSegment: (segmentId: string, value: string) => void;
  appendTag: (payload: Record<string, unknown>) => void;
  removeSegment: (segmentId: string) => void;
  clear: () => void;
  /** Focus the composer (last text field after layout). */
  focusComposer: () => void;
  /** Ref to attach to the composer root for focus management. */
  composerRootRef: React.RefObject<HTMLDivElement | null>;
}

const DocumentChatComposerContext = createContext<
  DocumentChatComposerContextValue | undefined
>(undefined);

const initialSegments = (): ChatComposerSegment[] => [
  { id: newSegmentId(), type: 'text', value: '' },
];

export function DocumentChatComposerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [segments, setSegments] =
    useState<ChatComposerSegment[]>(initialSegments);
  const composerRootRef = useRef<HTMLDivElement | null>(null);

  const updateTextSegment = useCallback((segmentId: string, value: string) => {
    setSegments((prev) =>
      prev.map((s) =>
        s.id === segmentId && s.type === 'text' ? { ...s, value } : s,
      ),
    );
  }, []);

  const appendTag = useCallback((payload: Record<string, unknown>) => {
    setSegments((prev) => {
      const next = [...prev];
      if (next.length === 0) {
        next.push({ id: newSegmentId(), type: 'text', value: '' });
      }
      const last = next[next.length - 1];
      if (last.type === 'tag') {
        next.push({ id: newSegmentId(), type: 'tag', payload: { ...payload } });
        next.push({ id: newSegmentId(), type: 'text', value: '' });
      } else {
        next.push({ id: newSegmentId(), type: 'tag', payload: { ...payload } });
        next.push({ id: newSegmentId(), type: 'text', value: '' });
      }
      return next;
    });
  }, []);

  const removeSegment = useCallback((segmentId: string) => {
    setSegments((prev) => {
      const next = prev.filter((s) => s.id !== segmentId);
      return mergeAdjacentText(next.length > 0 ? next : initialSegments());
    });
  }, []);

  const clear = useCallback(() => {
    setSegments(initialSegments());
  }, []);

  const focusComposer = useCallback(() => {
    requestAnimationFrame(() => {
      const root = composerRootRef.current;
      if (!root) return;
      const inputs = root.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement
      >(
        'input[data-chat-composer-text="true"], textarea[data-chat-composer-text="true"]',
      );
      const last = inputs[inputs.length - 1];
      last?.focus();
    });
  }, []);

  const value = useMemo(
    () => ({
      segments,
      setSegments,
      updateTextSegment,
      appendTag,
      removeSegment,
      clear,
      focusComposer,
      composerRootRef,
    }),
    [
      segments,
      updateTextSegment,
      appendTag,
      removeSegment,
      clear,
      focusComposer,
    ],
  );

  return (
    <DocumentChatComposerContext.Provider value={value}>
      {children}
    </DocumentChatComposerContext.Provider>
  );
}

export function useDocumentChatComposer() {
  const ctx = useContext(DocumentChatComposerContext);
  if (!ctx) {
    throw new Error(
      'useDocumentChatComposer must be used within DocumentChatComposerProvider',
    );
  }
  return ctx;
}
